import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import { TableKioskType } from '@api/TablesKiosks/TableKiosk';
import { toDateSafe } from '@utils/Date';

/**
 * Helpers PUROS del tablero de cocina (vista "Tablero" de /orders).
 *
 * Viven fuera de la pantalla porque de aquí dependen el semáforo de tiempo y el
 * reparto de columnas — es lo que se prueba con jest (OrdersBoard.test.ts) sin
 * montar React ni tocar Firestore. La pantalla solo pinta lo que estas
 * funciones deciden.
 */

export type OrderChannelKey = 'mostrador' | 'kiosko' | 'mesa' | 'pickup' | 'delivery';

export interface OrderChannel {
    key: OrderChannelKey;
    /** Nombre visible en la tarjeta: el canal de venta, o el nombre de la mesa/kiosko */
    label: string;
}

/**
 * Meta de tiempo por canal, en minutos. El cliente parado frente al mostrador
 * no espera lo mismo que un delivery: con un solo umbral global el mostrador se
 * ve verde cuando ya es tarde.
 */
export const BOARD_SLA_MINUTES: Record<OrderChannelKey, number> = {
    mostrador: 6,
    kiosko: 8,
    mesa: 10,
    pickup: 12,
    delivery: 15
};

/** Alarma dura: la misma regla de 15 min que ya usaba la lista (OrderLabelName) */
export const LATE_ALERT_MINUTES = 15;

/** Minutos que un pedido puede quedarse en "Listo" sin que nadie lo recoja */
const READY_WARN_MINUTES = 5;
const READY_LATE_MINUTES = 10;

// ⚠️ Su versión propia devolvía `undefined` para un Timestamp crudo, y sin
// fecha no hay alarma: el pedido retrasado no se marcaba. Es la del util.
const toDate = (value: any): Date | undefined => toDateSafe(value);

const diffMinutes = (from: Date, to: Date): number => Math.max(0, (to.getTime() - from.getTime()) / 60000);

/**
 * De dónde viene el pedido. OJO con el orden de los checks: la mesa/kiosko es
 * el origen FÍSICO y manda sobre todo lo demás; después el tipo de entrega
 * (que solo traen los pedidos de la web) y al final el canal de venta del POS.
 *
 * Limitación conocida: los pedidos hechos en un kiosko de la web llegan hoy
 * como Pickup sin `tableKiosk`, así que se ven como pickup. En cuanto la web
 * guarde el kiosko en la orden, esta función ya los distingue sin cambios.
 */
export const getOrderChannel = (order: Order): OrderChannel => {
    if (order.tableKiosk) {
        const isKiosk = order.tableKiosk.type === TableKioskType.KIOSK;
        return {
            key: isKiosk ? 'kiosko' : 'mesa',
            label: order.tableKiosk.name || (isKiosk ? 'Kiosko' : 'Mesa')
        };
    }
    if (order.deliveryType === DeliveryType.Delivery) return { key: 'delivery', label: 'Delivery' };
    if (order.deliveryType === DeliveryType.Pickup) return { key: 'pickup', label: 'Pickup' };
    return { key: 'mostrador', label: order.salesChannel?.name || 'Mostrador' };
};

export type BoardColumnKey = 'nuevos' | 'preparacion' | 'listo';

export const BOARD_COLUMNS: { key: BoardColumnKey; title: string; statuses: OrderStatus[] }[] = [
    { key: 'nuevos', title: 'Pedidos nuevos', statuses: [OrderStatus.Paid, OrderStatus.WaitingForPayment] },
    { key: 'preparacion', title: 'En preparación', statuses: [OrderStatus.Preparing] },
    { key: 'listo', title: 'Listo', statuses: [OrderStatus.Ready, OrderStatus.Route] }
];

/** Completed y Cancelled quedan fuera del tablero (Completed vive en la barra de entregados) */
export const getBoardColumn = (order: Order): BoardColumnKey | undefined =>
    BOARD_COLUMNS.find((column) => column.statuses.includes(order.status))?.key;

export type TakeAwayMode = 'aqui' | 'llevar' | 'mixto';

/**
 * `takeAway` es por LÍNEA, no por pedido (igual que el detalle del pedido, que separa
 * "para mostrador" de "para llevar"), así que la etiqueta de la tarjeta se
 * deriva. Solo aplica a pedidos en tienda: delivery y pickup siempre van.
 */
export const getTakeAwayMode = (order: Order): TakeAwayMode | undefined => {
    if (order.deliveryType) return undefined;
    const lines = (order.products || []).filter(Boolean);
    if (lines.length === 0) return undefined;
    const takeAway = lines.filter((line) => line.takeAway).length;
    if (takeAway === 0) return 'aqui';
    if (takeAway === lines.length) return 'llevar';
    return 'mixto';
};

export type BoardClockMode = 'programado' | 'cocina' | 'listo';
export type BoardSeverity = 'ok' | 'warn' | 'late';

export interface BoardClock {
    mode: BoardClockMode;
    /** Minutos transcurridos, o los que FALTAN cuando el pedido está programado */
    minutes: number;
    severity: BoardSeverity;
    slaMinutes: number;
    scheduledAt?: Date;
    /** Pasó los 15 min de la alarma dura */
    isLate: boolean;
}

export const getScheduledDate = (order: Order): Date | undefined =>
    order.isReservation ? toDate(order.reservationDate) : toDate(order.pickupDate);

/**
 * El reloj que se pinta en la tarjeta. Tres modos:
 * - `listo`: ya salió de cocina, lo que importa es cuánto lleva esperando a que
 *   lo recojan (comida enfriándose), contado desde el último cambio de estado.
 * - `programado`: pedido a futuro que todavía no debe entrar a cocina.
 * - `cocina`: el caso normal, contra la meta del canal.
 */
export const getBoardClock = (order: Order, now: Date = new Date()): BoardClock => {
    const slaMinutes = BOARD_SLA_MINUTES[getOrderChannel(order).key];
    const createdAt = toDate(order.date) || now;
    const scheduledAt = getScheduledDate(order);

    if (getBoardColumn(order) === 'listo') {
        const readyAt = toDate(order.lastUpdate) || createdAt;
        const minutes = diffMinutes(readyAt, now);
        return {
            mode: 'listo',
            minutes,
            slaMinutes: READY_LATE_MINUTES,
            severity: minutes >= READY_LATE_MINUTES ? 'late' : minutes >= READY_WARN_MINUTES ? 'warn' : 'ok',
            scheduledAt,
            isLate: false
        };
    }

    // Un pedido programado arranca su reloj una meta ANTES de la hora pactada, no
    // cuando el cliente lo hizo: si no, un pickup de las 8pm pedido a mediodía
    // nace en rojo y llena el tablero de alarmas falsas.
    const startAt =
        scheduledAt && scheduledAt.getTime() > createdAt.getTime()
            ? new Date(scheduledAt.getTime() - slaMinutes * 60000)
            : createdAt;

    if (now.getTime() < startAt.getTime()) {
        return {
            mode: 'programado',
            minutes: diffMinutes(now, startAt),
            slaMinutes,
            severity: 'ok',
            scheduledAt,
            isLate: false
        };
    }

    const minutes = diffMinutes(startAt, now);
    return {
        mode: 'cocina',
        minutes,
        slaMinutes,
        severity: minutes >= slaMinutes ? 'late' : minutes >= slaMinutes * 0.6 ? 'warn' : 'ok',
        scheduledAt,
        isLate: minutes >= LATE_ALERT_MINUTES
    };
};

/** mm:ss para el cronómetro de la tarjeta (arriba de una hora, h:mm) */
export const formatBoardClock = (minutes: number): string => {
    const totalSeconds = Math.floor(minutes * 60);
    if (totalSeconds >= 3600) {
        const hours = Math.floor(totalSeconds / 3600);
        return `${hours}:${String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')} h`;
    }
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

/** El número corto que se ve en la lista: la secuencia del shortId */
export const getOrderShortNumber = (order: Order): string => {
    const sequence = Number((order.shortId || '').split('-')[1]);
    return Number.isFinite(sequence) ? String(sequence).padStart(2, '0') : order.shortId || '';
};
